import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'classnames';

import Logger from '../../../../services/Logger';
import DetailsPanelEditHeader from './DetailsPanelEditHeader';
import DetailsPanelEditSortableList from './DetailsPanelEditSortableList';
import DetailsPanelEditList from './DetailsPanelEditList';
import './styles.scss';

import { updateHidden } from './utils';

const DetailsPanelEdit = (props) => {
  const {
    isOpen, fields, groups, config, toggleEditPanel, updateConfig, resetToDefault, permissions,
  } = props;
  const listRef = useRef();
  const [isDragging, setDrag] = useState(false);
  const { hidden } = config;
  const checkedFieldsSize = fields.length - hidden.length;
  const isAllChecked = checkedFieldsSize === fields.length;

  const handleToggleField = useCallback(
    (checked, ids) => {
      Logger.log('User', 'CustomizeViewMetadataFieldsSelected', { [ids]: checked });

      const updatedHidden = hidden.length ? updateHidden(hidden, checked, ids) : ids;
      updateConfig(groups, updatedHidden);
    },
    [groups, hidden, updateConfig],
  );

  const handleDragStart = useCallback(() => {
    setDrag(true);
    document.body.classList.add('dragging');
  }, []);

  const handleDragEnd = useCallback(({ oldIndex, newIndex, updatedGroups }) => {
    if (typeof oldIndex === 'number' && typeof newIndex === 'number') {
      updateConfig(updatedGroups, hidden);
    }

    setDrag(false);
    document.body.classList.remove('dragging');
  }, [hidden, updateConfig]);

  const handleSelect = useCallback(() => {
    Logger.log('User', 'CustomizeViewAllMetadataFieldsChecked');
    if (!isAllChecked) {
      updateConfig(groups, []);
    } else {
      const hidden = fields.map(({ id, title }) => id || title);

      updateConfig(groups, hidden);
    }
  }, [fields, groups, isAllChecked, updateConfig]);

  const scrollWidth = listRef.current?.offsetWidth - listRef.current?.clientWidth;

  return (
    <div
      className={clsx('detailsPanelEdit', {
        'detailsPanelEdit--opened': isOpen,
        'detailsPanelEdit--dragging': isDragging,
      })}
    >
      <DetailsPanelEditHeader
        isAllChecked={isAllChecked}
        checkedFieldsSize={checkedFieldsSize}
        onSelect={handleSelect}
        toggleEditPanel={toggleEditPanel}
        scrollWidth={scrollWidth}
        isNotEmptyList={!!groups.length}
        resetToDefault={resetToDefault}
      />
      <DetailsPanelEditSortableList
        groups={groups}
        onSortStart={handleDragStart}
        onSortEnd={handleDragEnd}
      >
        <div ref={listRef} className="detailsPanelEdit__list">
          <DetailsPanelEditList listId="checked" groups={groups} hidden={hidden} toggleField={handleToggleField} permissions={permissions} />
        </div>
      </DetailsPanelEditSortableList>
    </div>
  );
};

DetailsPanelEdit.defaultProps = {
  fields: [],
  groups: [],
};
DetailsPanelEdit.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      permission: PropTypes.string,
    }),
  ),
  groups: PropTypes.array,
  config: PropTypes.shape({
    order: PropTypes.arrayOf(PropTypes.string).isRequired,
    hidden: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  setFields: PropTypes.func.isRequired,
  updateConfig: PropTypes.func.isRequired,
  toggleEditPanel: PropTypes.func.isRequired,
};

export default DetailsPanelEdit;
